import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import { usePriceToString } from '@/shared/hooks/usePriceToStringFormatter'
import type { EnrichedContractTemplate } from '@/shared/services/campaignDataResolver'
import { logError } from '@/shared/utils/error'
import { SalesCampaignMisconfigurationError } from '@/shared/utils/errorClasses'
import { isEnergyElement, isMonthlyFeeElement } from '@/shared/utils/tariffElementUtils'

import { getCurrentPrice } from '../../Price/utils'

export type UseVatPercentageTextProps = {
  contractTemplate: EnrichedContractTemplate
}

/**
 * Hook that return vatPercentage text base on the contract template information
 */
export const useVatPercentageText = ({ contractTemplate }: UseVatPercentageTextProps) => {
  const { formatPrice } = usePriceToString()
  const t = useTranslations('purchaseFlow')

  const vatPercentageText = useMemo(() => {
    const { customerType, tariffElements, campaignId, productName, tariffNo } = contractTemplate
    const isVatExcluded = customerType === 'ENTERPRISE'

    const energyElement = tariffElements.find((el) => isEnergyElement(el.type))
    const monthlyFeeElement = tariffElements.find((el) => isMonthlyFeeElement(el.type))
    const energyPriceElement = getCurrentPrice(energyElement?.prices)
    const monthlyFeePriceElement = getCurrentPrice(monthlyFeeElement?.prices)
    const vatPercentage = energyPriceElement?.vatPercentage ?? monthlyFeePriceElement?.vatPercentage

    if (!Number.isNaN(vatPercentage)) {
      const text = t(`vatText`, {
        vatPercentage: formatPrice(isVatExcluded ? 0 : vatPercentage, { minimumFractionDigits: 0 }),
      })

      return text
    }
    logError(
      new SalesCampaignMisconfigurationError(
        'useVatPercentageText hook cannot find vat percentage information for contract',
        {
          campaignId: campaignId,
          contractProductName: productName,
          tariffNo: tariffNo,
        },
      ),
    )
    return ''
  }, [contractTemplate, formatPrice, t])

  return { vatPercentageText }
}
